/**
 * Tandem console — "Engine settings" (PHASE 2). The auto-booking engine does not
 * exist yet, so nothing here is connected: these are the intended policy controls,
 * kept so the rules can be agreed first. Hideable via Settings, Console pages.
 */
import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { T } from "./tokens";
import { Panel } from "./ui";

const sqBtn = {
    width: "26px",
    height: "26px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "6px",
    border: `1px solid ${T.border}`,
    background: T.card,
    color: T.ink2,
    fontSize: "15px",
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: T.font,
    "&:hover": { background: T.rowTint },
} as const;

const rowLabel = { fontSize: "13px", color: T.ink2 } as const;

const Stepper = ({ value, onStep }: { value: string; onStep: (d: number) => void }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Box component="button" onClick={() => onStep(-1)} sx={sqBtn}>−</Box>
        <Typography sx={{ fontWeight: 700, minWidth: "62px", textAlign: "center", fontSize: "13px", fontVariantNumeric: "tabular-nums" }}>
            {value}
        </Typography>
        <Box component="button" onClick={() => onStep(1)} sx={sqBtn}>+</Box>
    </Box>
);

const Segmented = ({
    options,
    unit,
    value,
    onChange,
}: {
    options: readonly string[];
    unit: string;
    value: string;
    onChange: (v: string) => void;
}) => (
    <Box sx={{ display: "flex", border: `1px solid ${T.border}`, borderRadius: "8px", overflow: "hidden" }}>
        {options.map((o) => (
            <Box
                key={o}
                component="button"
                onClick={() => onChange(o)}
                sx={{
                    border: "none",
                    padding: "6px 12px",
                    fontSize: "12px",
                    fontWeight: 700,
                    fontFamily: T.font,
                    cursor: "pointer",
                    background: value === o ? T.ink : T.card,
                    color: value === o ? "#fff" : T.muted,
                    "&:hover": { background: value === o ? T.ink : T.rowTint },
                }}
            >
                {o}{unit}
            </Box>
        ))}
    </Box>
);

const ESCALATION_RULES = [
    "Double visits at the same client",
    "Any visit in the top complexity band (needs a complexity measure — none exists yet)",
    "First visit for a new client",
    "Stacks using over 90% of the drive cap",
    "Travel-heavy solo bookings (round-trip drive exceeds time on site)",
];

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

const EngineSettingsScreen = () => {
    const [horizon, setHorizon] = useState(3);
    const [drive, setDrive] = useState(35);
    const [complexity, setComplexity] = useState(6);
    const [maxDaily, setMaxDaily] = useState(240);
    const [sweep, setSweep] = useState("30");
    const [override, setOverride] = useState("48");
    const [escalations, setEscalations] = useState<boolean[]>([true, true, true, false, true]);

    return (
        <Box sx={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: "18px" }}>
            <Box sx={{ background: T.amberTint, border: `1px solid ${T.amberBorder}`, color: T.amber, borderRadius: "10px", padding: "10px 14px", fontSize: "12.5px", fontWeight: 700 }}>
                Phase 2 — the auto-booking engine is not built yet. These controls are not connected to
                anything; they are here so the policy can be agreed up front.
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: "18px", alignItems: "start" }}>
                <Panel title="Booking policy" sub="The limits the engine would book within">
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        {[
                            { label: "Auto-book horizon", value: `${horizon} wks`, step: (d: number) => setHorizon((n) => clamp(n + d, 1, 12)) },
                            { label: "Max drive between paired sites", value: `${drive} min`, step: (d: number) => setDrive((n) => clamp(n + d * 5, 10, 90)) },
                            { label: "Combined complexity ceiling", value: `${complexity}`, step: (d: number) => setComplexity((n) => clamp(n + d, 2, 8)) },
                            { label: "Max daily driving", value: `${maxDaily} min`, step: (d: number) => setMaxDaily((n) => clamp(n + d * 15, 60, 480)) },
                        ].map((r) => (
                            <Box key={r.label} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                                <Typography sx={rowLabel}>{r.label}</Typography>
                                <Stepper value={r.value} onStep={r.step} />
                            </Box>
                        ))}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", pt: "4px" }}>
                            <Typography sx={rowLabel}>Sweep frequency</Typography>
                            <Segmented options={["15", "30", "60"]} unit=" min" value={sweep} onChange={setSweep} />
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                            <Typography sx={rowLabel}>Override window</Typography>
                            <Segmented options={["24", "48", "72"]} unit=" h" value={override} onChange={setOverride} />
                        </Box>
                    </Box>
                </Panel>

                <Panel title="Needs human approval" sub="Bookings the engine would never make on its own">
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                        {ESCALATION_RULES.map((label, i) => (
                            <Box
                                key={label}
                                onClick={() => setEscalations((prev) => prev.map((v, j) => (j === i ? !v : v)))}
                                sx={{ display: "flex", alignItems: "center", gap: "10px", padding: "9px 0", cursor: "pointer", borderTop: i === 0 ? "none" : `1px solid ${T.borderSoft}` }}
                            >
                                <Box
                                    sx={{
                                        width: "17px",
                                        height: "17px",
                                        borderRadius: "5px",
                                        flexShrink: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "11px",
                                        fontWeight: 800,
                                        color: "#fff",
                                        background: escalations[i] ? T.ink : T.card,
                                        border: `1.5px solid ${escalations[i] ? T.ink : "#B7BED2"}`,
                                    }}
                                >
                                    {escalations[i] ? "✓" : ""}
                                </Box>
                                <Typography sx={{ fontSize: "13px", color: T.ink2 }}>{label}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Panel>
            </Box>
        </Box>
    );
};

export default EngineSettingsScreen;
